// src/components/DailyOracleDeck.js

import React, { useState, useEffect } from 'react';
import { SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import './DailyOracleDeck.css'; // Crea este archivo si necesitas estilos específicos

// Helper para formatear fechas
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options); // 'en-US' para formato en inglés
};

// Define un CommonLogData genérico aquí para que el componente sea más autónomo
const Common = { getCommonLogData: (path) => ({ page: path, timestamp: new Date().toISOString() }) };

const DailyOracleDeck = ({ 
  sacredCircuits, 
  symbolImages, 
  maxDraws = 3, // Configurable: por defecto 3 cartas al día
  userEmail = '', // Email del usuario, asumimos que viene de props
  apiUrls, // Objeto con { card_draw_log, card_draw_history }
  sendCodedLog = (log) => console.log("Logging (DailyOracleDeck):", log), // Función para enviar logs
  locationPath = '/daily-oracle' // Path de la página donde se usa, para los logs
}) => {
  const [dailyDrawnCards, setDailyDrawnCards] = useState([]); // Cartas sacadas hoy
  const [drawsMadeToday, setDrawsMadeToday] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);
  const [history, setHistory] = useState([]); // Historial de cartas
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState('');

  const LOCAL_STORAGE_KEY_CARDS = 'daily_oracle_drawn_cards';
  const LOCAL_STORAGE_KEY_DATE = 'daily_oracle_draw_date';

  // --- Cargar estado inicial y historial ---
  useEffect(() => {
    const loadDailyDraws = () => {
      const savedCardsJSON = localStorage.getItem(LOCAL_STORAGE_KEY_CARDS);
      const savedDate = localStorage.getItem(LOCAL_STORAGE_KEY_DATE);
      const today = new Date().toISOString().split('T')[0];

      if (savedCardsJSON && savedDate === today) {
        const parsedCards = JSON.parse(savedCardsJSON);
        setDailyDrawnCards(parsedCards);
        setDrawsMadeToday(parsedCards.length);
      } else {
        // Nuevo día o datos viejos, reiniciar
        localStorage.removeItem(LOCAL_STORAGE_KEY_CARDS);
        localStorage.removeItem(LOCAL_STORAGE_KEY_DATE);
        setDailyDrawnCards([]);
        setDrawsMadeToday(0);
      }
    };

    loadDailyDraws();
    // Solo intenta cargar el historial si hay un email de usuario y la URL de la API está disponible
    if (userEmail && apiUrls?.card_draw_history) {
      fetchHistory();
    }
  }, [userEmail, apiUrls?.card_draw_history]); // Dependencias para re-ejecutar si cambian

  // --- Fetch del historial ---
  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    setError('');
    try {
      if (!apiUrls.card_draw_history) {
        throw new Error('Card history API URL is not configured.');
      }
      if (!userEmail) {
        throw new Error('User email is required to fetch history.');
      }

      const response = await fetch(apiUrls.card_draw_history, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          days: 7, // Última semana
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch card history.');
      }

      const data = await response.json();
      // Data debería ser un array de objetos { date: 'YYYY-MM-DD', cards: [{card_id, position, timestamp}, ...] }
      setHistory(data.history || []);
      sendCodedLog({
        message: 'Daily Oracle history fetched successfully',
        event: 'daily_oracle_history_fetch_success',
        ...Common.getCommonLogData(locationPath)
      });
    } catch (err) {
      console.error("Error fetching history:", err);
      setError('Could not load history: ' + err.message);
      sendCodedLog({
        message: `Daily Oracle history fetch failed: ${err.message}`,
        event: 'daily_oracle_history_fetch_error',
        ...Common.getCommonLogData(locationPath)
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // --- Enviar registro de carta al backend ---
  const logCardDrawToBackend = async (card, position) => {
    if (!apiUrls?.card_draw_log || !userEmail) {
      console.warn("API URL or user email not provided for logging card draw.");
      return;
    }

    try {
      const response = await fetch(apiUrls.card_draw_log, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          card_id: card.id,
          card_name: card.name,
          position: position, // 1, 2, 3...
          draw_date: new Date().toISOString(),
          page: locationPath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to log card draw.');
      }
      sendCodedLog({
        message: `Card ${card.name} (pos ${position}) drawn and logged.`,
        event: 'daily_oracle_draw_success',
        card_id: card.id,
        position: position,
        ...Common.getCommonLogData(locationPath)
      });
    } catch (err) {
      console.error("Error logging card draw:", err);
      sendCodedLog({
        message: `Failed to log card ${card.name} (pos ${position}): ${err.message}`,
        event: 'daily_oracle_draw_error',
        card_id: card.id,
        position: position,
        ...Common.getCommonLogData(locationPath)
      });
    }
  };

  // --- Lógica de extracción de carta ---
  const handleDrawCard = () => {
    if (drawsMadeToday >= maxDraws || isRevealing) return;

    setIsRevealing(true);
    setTimeout(() => {
      // Filtrar cartas ya sacadas hoy para evitar duplicados si es posible
      const previouslyDrawnCardIds = dailyDrawnCards.map(d => d.card.id);
      const availableCircuits = sacredCircuits.filter(
        card => !previouslyDrawnCardIds.includes(card.id)
      );

      let newCard;
      if (availableCircuits.length > 0) {
        // Si hay cartas únicas disponibles, elige una de ellas
        const randomIndex = Math.floor(Math.random() * availableCircuits.length);
        newCard = availableCircuits[randomIndex];
      } else {
        // Si ya se sacaron todas las cartas únicas, permite duplicados del mazo completo
        const randomIndex = Math.floor(Math.random() * sacredCircuits.length);
        newCard = sacredCircuits[randomIndex];
      }
      
      const newDrawPosition = drawsMadeToday + 1;
      const newDrawnCardEntry = {
        card: newCard,
        position: newDrawPosition,
        timestamp: new Date().toISOString(),
      };

      const updatedCards = [...dailyDrawnCards, newDrawnCardEntry];
      const today = new Date().toISOString().split('T')[0];

      localStorage.setItem(LOCAL_STORAGE_KEY_CARDS, JSON.stringify(updatedCards));
      localStorage.setItem(LOCAL_STORAGE_KEY_DATE, today);

      setDailyDrawnCards(updatedCards);
      setDrawsMadeToday(newDrawPosition);
      setIsRevealing(false);

      // Enviar la información de la carta al backend
      logCardDrawToBackend(newCard, newDrawPosition);

    }, 1000); // Duración de la animación
  };

  // Si no hay email de usuario, mostramos un mensaje para loguearse
  if (!userEmail) {
    return (
      <div className="text-center w-full max-w-xl mx-auto mb-16 p-8 bg-gray-900/50 border border-red-700/50 rounded-xl shadow-2xl shadow-red-500/20 backdrop-blur-sm">
        <h2 className="text-3xl font-bold mb-4 text-red-400">Login Required</h2>
        <p className="text-lg text-gray-300">
          Please log in or provide your email to access the Daily Oracle Deck and track your insights.
        </p>
        {/* Aquí podrías añadir un botón para redirigir al login o al formulario de email */}
      </div>
    );
  }

  return (
    <div className="text-center w-full max-w-4xl mx-auto mb-16" id="daily-oracle-deck">
      <h2 className="text-3xl font-bold mb-4">
        Your Daily Sacred Circuitry Focus ({drawsMadeToday} / {maxDraws} drawn)
      </h2>
      <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
        Draw up to {maxDraws} symbols daily to guide your focus and intention. Each draw helps you build a relationship with these powerful energies.
      </p>

      {/* Área de las cartas sacadas hoy */}
      <div className="flex flex-wrap justify-center items-stretch gap-6 mb-8">
        {Array.from({ length: maxDraws }).map((_, index) => {
          const cardEntry = dailyDrawnCards[index];
          const isDrawn = !!cardEntry;
          // La carta activa es la siguiente a sacar, si no se han sacado todas
          const isActiveSlot = index === drawsMadeToday && drawsMadeToday < maxDraws;
          // Las cartas anteriores a la activa están "habilitadas" o ya sacadas
          const isEnabledForDraw = index === drawsMadeToday; // Solo se habilita si es la posición actual
          // Si no es la primera carta, y la anterior no se ha sacado, no está habilitada
          const previousCardDrawn = index === 0 || dailyDrawnCards[index - 1];
          const canInteractWithSlot = isEnabledForDraw && previousCardDrawn;

          return (
            <div 
              key={index} 
              className={`w-48 h-64 bg-gray-800 border-2 rounded-xl flex flex-col items-center justify-center p-4 
                          relative transition-all duration-300
                          ${isDrawn 
                              ? 'border-purple-500 bg-gray-900 fade-in-animation' 
                              : `opacity-60 ${canInteractWithSlot ? 'border-gray-600' : 'border-gray-800'}`
                          }
                          ${isActiveSlot && !isRevealing && canInteractWithSlot 
                              ? 'border-sky-500 scale-105 shadow-lg shadow-sky-500/30' 
                              : ''}
                          ${!canInteractWithSlot && !isDrawn ? 'pointer-events-none' : ''}
                          ${isRevealing && isActiveSlot ? 'animate-pulse' : ''}
              `}
            >
              {isDrawn ? (
                <>
                  <img 
                    src={symbolImages[cardEntry.card.id - 1]}
                    alt={cardEntry.card.name}
                    className="w-24 h-24 object-contain mb-2"
                  />
                  <h4 className="text-lg font-bold text-sky-400">{cardEntry.card.name}</h4>
                  <p className="text-gray-400 text-xs mt-1">Position: {cardEntry.position}</p>
                </>
              ) : (
                <div className="text-gray-500 flex flex-col items-center">
                  <SparklesIcon className="w-12 h-12 mb-2"/>
                  <span className="text-sm">
                    {isEnabledForDraw && previousCardDrawn ? 'Click to Draw' : 'Next Card'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Botón para sacar carta */}
      {drawsMadeToday < maxDraws && (
        <button 
          onClick={handleDrawCard}
          disabled={isRevealing || drawsMadeToday >= maxDraws}
          className="py-3 px-8 bg-gradient-to-r from-purple-600 to-sky-500 hover:from-purple-700 hover:to-sky-600 rounded-lg font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
        >
          {isRevealing ? 'Revealing...' : `Draw Card ${drawsMadeToday + 1} of ${maxDraws}`}
        </button>
      )}
      {drawsMadeToday >= maxDraws && (
        <p className="text-lg text-gray-400 mt-4">You've drawn all your cards for today. Come back tomorrow!</p>
      )}
      
      {error && <p className="text-red-400 mt-4">{error}</p>}

      {/* Botón y sección de historial */}
      {history.length > 0 && ( // Solo muestra el botón si hay historial cargado
        <div className="mt-12 w-full max-w-2xl mx-auto">
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-center gap-2 py-2 px-6 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold text-gray-300 transition-all duration-300 mx-auto"
          >
            {showHistory ? (
              <>
                Hide History <ChevronUpIcon className="w-5 h-5"/>
              </>
            ) : (
              <>
                View Last Week's History <ChevronDownIcon className="w-5 h-5"/>
              </>
            )}
          </button>

          {showHistory && (
            <div className="mt-6 p-6 bg-gray-900/50 border border-gray-700 rounded-xl text-left fade-in-animation">
              <h3 className="text-2xl font-bold mb-4 text-sky-400">Your Drawing History (Last 7 Days)</h3>
              {isLoadingHistory ? (
                <p className="text-gray-400">Loading history...</p>
              ) : history.length === 0 ? (
                <p className="text-gray-400">No draw history found for the last week.</p>
              ) : (
                <div className="space-y-6">
                  {history.map((dayEntry, index) => (
                    <div key={index} className="border-b border-gray-700 pb-4 last:border-b-0 last:pb-0">
                      <h4 className="text-xl font-bold text-purple-300 mb-3">{formatDate(dayEntry.date)}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {dayEntry.cards.sort((a,b) => a.position - b.position).map((cardData, cardIndex) => ( // Ordenar por posición
                          <div key={cardIndex} className="flex items-center bg-gray-800 p-3 rounded-md shadow">
                            <img 
                              src={symbolImages[cardData.card_id - 1]}
                              alt={sacredCircuits.find(c => c.id === cardData.card_id)?.name || 'Symbol'}
                              className="w-10 h-10 object-contain mr-3 flex-shrink-0"
                            />
                            <div>
                              <p className="font-semibold text-white leading-tight">
                                {sacredCircuits.find(c => c.id === cardData.card_id)?.name || `Symbol ${cardData.card_id}`}
                              </p>
                              <p className="text-gray-400 text-sm">Position: {cardData.position}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyOracleDeck;